import {Fragment, type ChangeEvent} from 'react'
import {QuestionType, type Question, type Questionnaire} from '@common/models'

import {BookingQuestion} from './BookingQuestion'
import {BoldRedText} from './BookingQuestion.styled'
import {BookingChoiceInput} from './BookingChoiceInput'
import {BookingDateInput} from './BookingDateInput'
import type {PreQuestionnaireAnswers} from './preQuestionnaireAnswers'

import {Container} from '~/components/general/container/Container'
import {Input} from '~/components/general/input/Input'

export type {PreQuestionnaireAnswers}

type Props = {
  questionnaire: Questionnaire
  answers: PreQuestionnaireAnswers
  onChange: (
    questionId: string,
    answer: {value?: string; choices?: string[]},
  ) => void
}

// Renders bold segments delimited by ** in a purely presentational way.
// The original text is not modified and is preserved for payloads elsewhere.
const renderWithBoldAsterisks = (text?: string) => {
  if (!text) return null
  const parts = text.split('**')
  return parts.map((part, idx) =>
    idx % 2 === 1 ? (
      <BoldRedText key={idx}>{part}</BoldRedText>
    ) : (
      <Fragment key={idx}>{part}</Fragment>
    ),
  )
}

const renderQuestion = (
  q: Question,
  value: {value?: string; choices?: string[]} | undefined,
  onChange: (answer: {value?: string; choices?: string[]}) => void,
) => {
  switch (q.type) {
    case QuestionType.TEXT:
      return (
        <Input
          value={value?.value ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange({value: e.target.value})
          }
          required={!q.isOptional}
          placeholder={q.placeholder}
        />
      )
    case QuestionType.CHOICE: {
      const choices = (q.choices || []).map((c) => ({id: c.id, label: c.label}))
      return (
        <BookingChoiceInput
          choices={choices}
          selectedId={value?.choices?.[0]}
          name={q.id}
          required={!q.isOptional}
          onChange={(choiceId) => onChange({choices: [choiceId]})}
        />
      )
    }
    case QuestionType.DATE:
      return (
        <BookingDateInput
          value={value?.value}
          required={!q.isOptional}
          onChange={(v) => onChange({value: v})}
        />
      )
    default:
      return null
  }
}

export const PreQuestionnaireRenderer = ({
  questionnaire,
  answers,
  onChange,
}: Props) => {
  const qs = questionnaire?.questions || []
  if (!qs.length) return null

  return (
    <Container direction="column" spacing={2}>
      {qs.map((q) => (
        <BookingQuestion
          key={q.id}
          title={renderWithBoldAsterisks(q.title)}
          subtitle={
            q.subtitle ? renderWithBoldAsterisks(q.subtitle) : undefined
          }>
          {renderQuestion(q, answers[q.id], (ans) => onChange(q.id, ans))}
        </BookingQuestion>
      ))}
    </Container>
  )
}
